// SignUp.js  – session-token flow added, UI unchanged
import {
	Box, Typography, Card, CardHeader, CardContent, IconButton, TextField, FormControl, InputLabel, Select,
	MenuItem, Button, Alert, LinearProgress, CardActionArea, Dialog, DialogTitle, DialogContent,
	DialogContentText, DialogActions, Slide, CircularProgress, useMediaQuery, Backdrop, Collapse, Avatar, useTheme,
} from "@mui/material";
import {
	ArrowBackIosNew, ArrowForwardIos, Visibility, VisibilityOff, MailOutline, Phone, Person, Business,
	Language, AutoAwesome, CheckCircle, ErrorOutline, Check, Storefront, Timer,
} from "@mui/icons-material";
import {useNavigate} from "react-router-dom";
import React, {useState, useEffect, useRef, useMemo, Fragment, forwardRef} from "react";
import countries from "../SignUp/countries";
import {ThemeProvider} from "@mui/material/styles";
import {theme} from "../../theme";
import Logo from "../../Logo_No_Bg.png";
import { config } from "../../config";
import { aiErrorMessage } from "../../utils/aiErrors";
import { normalizeSessionExpiry, setAuthSession } from "../../utils/authSession";
import { OTP_COOLDOWN_SECONDS, resolveOtpStatus, useCountdown } from "../../utils/authApi";


const OutlinedButton = (props) => (
	<Button
		variant="outlined"
		color="secondary"
		{...props}
		sx={{
			fontWeight: 600,
			...props.sx,
		}}
	/>
);

const Transition = forwardRef(function Transition(props, ref) {
	return <Slide direction="up" ref={ref} {...props} />;
});

/* ═══════════════════════════════ component ═════════════════════════════ */
export default function SignUp() {
	const navigate = useNavigate();
	const desktop = useMediaQuery("(min-width:900px)");
	const themes = useTheme(theme);
	/* ──────────────── original state (untouched) ──────────────── */
	const [step, setStep] = useState("step1");
	const stepNum = useMemo(() => ({step1: 1, step2: 2, step3: 3, step4: 4, step5: 5}[step]), [step]);

	// Account creation
	const [vendorId, setVendorId] = useState("");
	const [sessionToken, setSessionToken] = useState("");        // NEW
	const [email, setEmail] = useState("");
	const [countryCode, setCountryCode] = useState("");
	const [countryDail, setCountryDail] = useState("");
	const [countryName, setCountryName] = useState("");
	const [phoneNumber, setPhoneNumber] = useState("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showPwd, setShowPwd] = useState(false);
	const [showPwd2, setShowPwd2] = useState(false);
	const [emailErr, setEmailErr] = useState(null);
	const [notice, setNotice] = useState({type: "", message: ""});
	const [otp, setOtp] = useState(Array(6).fill(""));
	const [otpValue, setOtpValue] = useState("");

	const {remaining: remainingTime, start: startTimer} = useCountdown(OTP_COOLDOWN_SECONDS);
	const timerRunning = remainingTime > 0;
	const otpInputs = useRef([]);

	// Personal & Business
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [address1, setAddress1] = useState("");
	const [address2, setAddress2] = useState("");
	const [city, setCity] = useState("");
	const [state, setState] = useState("");
	const [zipCode, setZipCode] = useState("");
	const [country, setCountry] = useState("");
	const [businessName, setBusinessName] = useState("");
	const [category, setCategory] = useState("");
	const [storeType, setStoreType] = useState("");
	const [storeDescription, setStoreDescription] = useState("");
	const [keyword, setKeyword] = useState("");

	// Domain
	const [baseDomain, setBaseDomain] = useState("");
	const [domain, setDomain] = useState("");
	const [domainList, setDomainList] = useState([]);
	const [loadingGenerateDomain, setLoadingGenerateDomain] = useState(false);
	const [openConsent, setOpenConsent] = useState(false);
	const [throwErrorDomain, setThrowErrorDomain] = useState(false);
	const [generateCount, setGenerateCount] = useState(0);
	const maxGenerateCount = 4;
	const [hideGenerateButton, setHideGenerateButton] = useState(false);
	const [isDomainAvailable, setIsDomainAvailable] = useState(false);
	const [previousDomain, setPreviousDomain] = useState("");
	const debounce = useRef(null);

	const debounceTimerRef = useRef(null);      // stores the setTimeout id
	const domainControllerRef = useRef(null);
	// Backdrop
	const [loading, setLoading] = useState(false);

	/* ──────────────── derived helpers ──────────────── */
	const passwordRegex =
		/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,20}$/;

	useEffect(() => {
		if (!notice.message) return;
		const t = setTimeout(() => setNotice({type: "", message: ""}), 6000);
		return () => clearTimeout(t);
	}, [notice.message]);

	const step1Valid =
		emailErr === "valid" &&
		email &&
		countryCode &&
		phoneNumber &&
		password &&
		confirmPassword &&
		password === confirmPassword;

	const step3Valid =
		firstName &&
		lastName &&
		address1 &&
		address2 &&
		state &&
		city &&
		zipCode &&
		country &&
		businessName &&
		category &&
		storeType &&
		storeDescription;

	const countFilled = (flags) => flags.filter(Boolean).length / flags.length;
	const stepFraction = (() => {
		switch (step) {
			case "step1":
				return countFilled([
					emailErr === "valid" && !!email,
					!!countryCode,
					!!phoneNumber,
					passwordRegex.test(password),
					!!confirmPassword && password === confirmPassword,
				]);
			case "step2":
				return otpValue.length / 6;
			case "step3":
				return countFilled([
					firstName, lastName, address1, address2, state, city,
					zipCode, country, businessName, category, storeType, storeDescription,
				].map((v) => !!String(v || "").trim()));
			case "step4":
				return domain && isDomainAvailable ? 1 : domain ? 0.5 : 0;
			default:
				return 1;
		}
	})();
	const progress = Math.min(100, ((stepNum - 1 + stepFraction) / 5) * 100);

	const formatTime = (t) => `${t.toString().padStart(2, "0")}`;

	/* ──────────────── OTP timers ──────────────── */
	useEffect(() => setOtpValue(otp.join("")), [otp]);

	const normalizeStoreUrl = (value) => {
		if (!value) return "";
		let s = String(value).trim();
		s = s.replace(/^(https?:\/\/)+/i, "");
		const bareToken = /^[a-z0-9-]+$/i;
		if (bareToken.test(s)) s = `${s}.microstores.net`;
		return `https://${s.toLowerCase()}`;
	};

	/* ──────────────── domain helpers ──────────────── */
	const handleGenerateDomain = (consent) => {
		setLoading(true);

		if (generateCount >= maxGenerateCount) {
			setLoading(false);
			return;
		}
		if (!businessName) {
			setThrowErrorDomain(true);
			setLoading(false);
			return;
		}

		setLoadingGenerateDomain(true);

		const endpoint = consent === "agree" ? "generate-domain" : "generate-domain-protected";

		const payload = {
			city,
			state,
			country,
			businessName,
			category,
			storeType,
			storeDescription,
			keyword,
		};
		if (consent === "agree") {
			payload.firstName = firstName;
			payload.lastName = lastName;
		}

		fetch(
			`${config.API_MWSSTOREMANAGERAI_BASE_URL}/api/v1/${endpoint}`,
			{
				method: "POST",
				body: JSON.stringify(payload),
				headers: {
					Accept: "*/*",
					"Cache-Control": "no-cache",
					"Content-Type": "application/json",
					"X-Session-Token": sessionToken,
				},
			}
		)
			.then((r) => {
				const aiMsg = aiErrorMessage(r.status);
				if (aiMsg) {
					const aiErr = new Error(aiMsg);
					aiErr.userMessage = aiMsg;
					throw aiErr;
				}
				return r.json();
			})
			.then((d) => {
				// d may be { domains: [...] } or something else. Be defensive.
				const suggestions =
					Array.isArray(d?.domains) ? d.domains
						: Array.isArray(d) ? d
							: Array.isArray(d?.data?.domains) ? d.data.domains
								: [];

				if (suggestions.length === 0) {
					throw new Error("No domain suggestions returned.");
				}

				// Normalize each suggestion to a full https URL
				const urls = suggestions.map((dom) => normalizeStoreUrl(dom));

				return fetch(
					`${config.API_MWSVENDORONBOARDING_BASE_URL}/microstores/domainAvailabilityCheck`,
					{
						method: "POST",
						body: JSON.stringify(urls),
						headers: {
							Accept: "*/*",
							"Cache-Control": "no-cache",
							"Content-Type": "application/json",
							"X-Session-Token": sessionToken,
						},
					}
				);
			})
			.then((r) => r.json())
			.then((availability) => {
				const arr = Array.isArray(availability) ? availability : [];
				const available = arr
					.filter((o) => o && o.status === "available" && o.domain)
					.map((o) => normalizeStoreUrl(o.domain));

				setDomainList(available);
				setGenerateCount((c) => c + 1);
			})
			.catch((err) => {
				setNotice({type: "error", message: err.userMessage || "Failed to generate domains. Please try again."});
			})
			.finally(() => {
				setLoadingGenerateDomain(false);
				setLoading(false);
			});
	};


	const fetchDomainAvailability = (domainStr, signal) => {
		setLoading(true);
		fetch(
			`${config.API_MWSVENDORONBOARDING_BASE_URL}/microstores/domainAvailabilityCheck`,
			{
				method: "POST",
				body: JSON.stringify([normalizeStoreUrl(domainStr)]),
				headers: {
					Accept: "*/*",
					"Cache-Control": "no-cache",
					"Content-Type": "application/json",
				},
				signal,
			}
		)
			.then((r) => r.json())
			.then((d) => {
				const first = Array.isArray(d) ? d[0] : null;
				setIsDomainAvailable(Boolean(first && first.status === "available"));
				setLoading(false);
			})
			.catch((e) => {
				if (e.name !== "AbortError") console.error(e);
				setLoading(false);
			});
	};

	/* ──────────────── OTP helpers ──────────────── */
	const handleOtpChange = (i, v) => {
		if (!/^\d?$/.test(v)) return;
		const newOtp = [...otp];
		newOtp[i] = v;
		setOtp(newOtp);
		if (v && i < 5) otpInputs.current[i + 1]?.focus();
	};
	const handleOtpKeyDown = (i, e) => {
		if (e.key === "Backspace" && !otp[i] && i > 0) otpInputs.current[i - 1]?.focus();
	};
	const handleOtpPaste = (e) => {
		e.preventDefault();
		const data = e.clipboardData
			.getData("text")
			.slice(0, 6)
			.split("")
			.filter((c) => /^\d$/.test(c));
		const newOtp = [...otp];
		data.forEach((c, i) => (newOtp[i] = c));
		setOtp(newOtp);
	};

	const resendOtp = () => {
		setOtp(Array(6).fill(""));
		setLoading(true);
		fetch(
			`${config.API_MWSVENDORONBOARDING_BASE_URL}/microstores/otp/send`,
			{
				method: "POST",
				body: JSON.stringify({vendorId}),
				headers: {
					Accept: "*/*",
					"Cache-Control": "no-cache",
					"Content-Type": "application/json",
				},
			}
		)
			.then((r) => r.json())
			.then((d) => {
				if (d.statusCode === 200) {
					startTimer();
				} else if (d.statusCode === 403) {
					setNotice({type: "error", message: d.message || "Please wait before requesting another code."});
					startTimer();
				} else {
					setNotice({type: "error", message: "Error sending OTP. Please try again."});
				}
			})
			.catch(() => setNotice({type: "error", message: "Network error while resending OTP."}))
			.finally(() => setLoading(false));
	};


	/* ──────────────── header text ──────────────── */
	const headTitle = {
		step1: "Create Your Account",
		step2: "Verify Your Email",
		step3: "Complete Your Profile",
		step4: "Choose Your Domain",
		step5: "Welcome to Microstores!",
	}[step];

	const headDesc = {
		step1: "Start your journey by creating your vendor account",
		step2: "Enter the verification code sent to your email",
		step3: "Tell us about yourself and your business",
		step4: "Select the perfect domain for your store",
		step5: "Your account has been created successfully",
	}[step];

	/* ═══════════════════════════════ UI ═══════════════════════════════ */
	return (
		<Box
			sx={{
				minHeight: "100svh", p: 3,
				background: "linear-gradient(135deg,#e8f0ff 0%,#f5f2ff 100%)",
				display: "flex", flexDirection: "column", alignItems: "center"
			}}
		>
			<ThemeProvider theme={themes}>
				<Box sx={{textAlign: "center", mb: 4}}>
					<Box
						sx={{
							mx: "auto", mb: 1, p: 1,
							borderRadius: 6, bgcolor: "transparent", userSelect: 'none',
							display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: 'none'
						}}
					>
						<img style={{width: 85, height: 85}} src={Logo}/>
					</Box>
					<Typography variant="h4" fontWeight={700}>
						Join Microstores
					</Typography>
					<Typography color="text.secondary" sx={{mt: 0.5}}>
						Create your online store in minutes
					</Typography>
				</Box>

				{/* -------- progress meta -------- */}
				<Box sx={{width: "100%", maxWidth: 640, mb: 1, display: "flex", justifyContent: "space-between"}}>
					<Typography variant="caption" fontWeight={600}>
						Step {stepNum} of 5
					</Typography>
					<Typography variant="caption" color="text.secondary">
						{Math.round(progress)}% Complete
					</Typography>
				</Box>
				<LinearProgress
					variant="determinate"
					value={progress}
					sx={{
						width: "100%", maxWidth: 640, height: 4,
						mb: 4, borderRadius: 2, bgcolor: "#d9e4ff",
						"& .MuiLinearProgress-bar": {bgcolor: "#2563eb"}
					}}
				/>
				{/* -------- main card -------- */}
				<Card sx={{width: "100%", maxWidth: 640, borderRadius: 3, boxShadow: 6}}>
					<CardHeader
						title={
							<Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
								<Box>
									<Typography variant="h6" fontWeight={700}>
										{headTitle}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										{headDesc}
									</Typography>
								</Box>
							</Box>
						}
					/>

					<CardContent sx={{display: "flex", flexDirection: "column", gap: 3}}>
						{/* ═══════════════════ STEP 1 ═══════════════════ */}
						{step === "step1" && (
							<Fragment>
								<TextField
									label="Email Address"
									value={email}
									onChange={(e) => {
										setEmail(e.target.value);
										const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.target.value);
										setEmailErr(ok ? "valid" : "invalid");
									}}
									error={email && emailErr === "invalid"}
									helperText={email && emailErr === "invalid" && "Enter a valid email"}
									fullWidth
									InputProps={{startAdornment: <MailOutline sx={{color: '#888', mr: 1}}/>}}
								/>

								<Box sx={{display: "flex", flexDirection: desktop ? "row" : "column", gap: 2}}>
									<FormControl sx={{minWidth: 160}}>
										<InputLabel>Country</InputLabel>
										<Select
											label="Country"
											value={countryDail}
											onChange={(e) => {
												const c = countries.find((co) => co.phone === e.target.value);
												setCountryCode(c.code);
												setCountryDail(c.phone);
												setCountryName(c.label);
											}}
											renderValue={(v) => (v ? `+${v}` : "")}
										>
											{countries.map((co) => (
												<MenuItem key={co.code} value={co.phone}>
													<img
														loading="lazy"
														width="20"
														src={`https://flagcdn.com/w20/${co.code.toLowerCase()}.png`}
														alt=""
														style={{marginRight: 8}}
													/>
													{co.label} +{co.phone}
												</MenuItem>
											))}
										</Select>
									</FormControl>

									<TextField
										label="Phone Number"
										value={phoneNumber}
										onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ""))}
										fullWidth
										InputProps={{startAdornment: <Phone sx={{color: '#888', mr: 1}}/>}}
									/>
								</Box>

								<TextField
									label="Password"
									type={showPwd ? "text" : "password"}
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									fullWidth
									InputProps={{
										endAdornment: (
											<IconButton onClick={() => setShowPwd((p) => !p)}>
												{showPwd ? <VisibilityOff/> : <Visibility/>}
											</IconButton>
										)
									}}
								/>

								<TextField
									label="Confirm Password"
									type={showPwd2 ? "text" : "password"}
									value={confirmPassword}
									onChange={(e) => setConfirmPassword(e.target.value)}
									error={confirmPassword && confirmPassword !== password}
									helperText={
										confirmPassword && confirmPassword !== password && "Passwords do not match"
									}
									fullWidth
									InputProps={{
										endAdornment: (
											<IconButton onClick={() => setShowPwd2((p) => !p)}>
												{showPwd2 ? <VisibilityOff/> : <Visibility/>}
											</IconButton>
										),
									}}
								/>
							</Fragment>
						)}

						{/* ═══════════════════ STEP 2 ═══════════════════ */}
						{step === "step2" && (
							<Fragment>
								<Typography align="center" color="text.secondary">
									We’ve sent a 6-digit verification code to <b>{email}</b>
								</Typography>

								<Box sx={{display: "flex", justifyContent: "center", gap: 1}}>
									{otp.map((d, i) => (
										<TextField
											key={i}
											inputRef={(el) => (otpInputs.current[i] = el)}
											value={d}
											onChange={(e) => handleOtpChange(i, e.target.value)}
											onKeyDown={(e) => handleOtpKeyDown(i, e)}
											onPaste={handleOtpPaste}
											inputProps={{
												maxLength: 1,
												style: {
													width: 40,
													height: 56,
													textAlign: "center",
													fontSize: 24,
													fontWeight: 600,
												},
											}}
										/>
									))}
								</Box>

								{timerRunning ? (
									<Typography
										variant="caption"
										display="flex"
										alignItems="center"
										justifyContent="center"
										gap={0.5}
										color="text.secondary"
									>
										<Timer fontSize="small"/> Resend in {formatTime(remainingTime)}s
									</Typography>
								) : (
									<OutlinedButton onClick={resendOtp}>
										Resend code
									</OutlinedButton>
								)}
							</Fragment>
						)}

						{/* ═══════════════════ STEP 3 ═══════════════════ */}
						{step === "step3" && (
							<Fragment>
								{/* Personal */}
								<Typography
									variant="subtitle1"
									fontWeight={700}
									display="flex"
									alignItems="center"
									gap={1}
								>
									<Person fontSize="small" color="secondary"/>
									Personal Information
								</Typography>

								<Box
									sx={{
										display: "grid",
										gridTemplateColumns: desktop ? "1fr 1fr" : "1fr",
										gap: 2,
									}}
								>
									<TextField label="First Name" value={firstName} onChange={(e) => setFirstName(e.target.value)}/>
									<TextField label="Last Name" value={lastName} onChange={(e) => setLastName(e.target.value)}/>
								</Box>

								<TextField
									label="Address Line 1"
									fullWidth
									value={address1}
									onChange={(e) => setAddress1(e.target.value)}
								/>
								<TextField
									label="Address Line 2"
									required
									fullWidth
									value={address2}
									onChange={(e) => setAddress2(e.target.value)}
								/>

								<Box
									sx={{
										display: "grid",
										gridTemplateColumns: desktop ? "1fr 1fr 1fr" : "1fr",
										gap: 2,
									}}
								>
									<TextField label="City" value={city} onChange={(e) => setCity(e.target.value)}/>
									<TextField label="State/Province" value={state} onChange={(e) => setState(e.target.value)}/>
									<TextField label="ZIP / Postal Code" value={zipCode} onChange={(e) => setZipCode(e.target.value)}/>
								</Box>
								<TextField label="Country" value={country} onChange={(e) => setCountry(e.target.value)} fullWidth/>

								{/* Business */}
								<Typography
									variant="subtitle1"
									mt={4}
									fontWeight={700}
									display="flex"
									alignItems="center"
									gap={1}
								>
									<Business fontSize="small" color="secondary"/>
									Business Information
								</Typography>

								<TextField
									label="Business Name"
									value={businessName}
									onChange={(e) => setBusinessName(e.target.value)}
									fullWidth
								/>

								<Box
									sx={{
										display: "grid",
										gridTemplateColumns: desktop ? "1fr 1fr" : "1fr",
										gap: 2,
									}}
								>
									<FormControl>
										<InputLabel>Business Category</InputLabel>
										<Select label="Business Category" value={category} onChange={(e) => setCategory(e.target.value)}>
											<MenuItem value="">None</MenuItem>
											{[
												"Groceries",
												"Fashion",
												"Consumer Electronics",
												"Books",
												"Toys, Hobbies & DIY",
												"Sports & Entertainment",
												"Furniture",
											].map((c) => (
												<MenuItem key={c} value={c}>
													{c}
												</MenuItem>
											))}
										</Select>
									</FormControl>

									<FormControl>
										<InputLabel>Store Type</InputLabel>
										<Select label="Store Type" value={storeType} onChange={(e) => setStoreType(e.target.value)}>
											<MenuItem value="">None</MenuItem>
											<MenuItem value="Retail">Retail</MenuItem>
											<MenuItem value="Wholesale">Wholesale</MenuItem>
										</Select>
									</FormControl>
								</Box>

								<TextField
									label="Store Description"
									value={storeDescription}
									onChange={(e) => setStoreDescription(e.target.value)}
									multiline
									maxRows={3}
									inputProps={{maxLength: 150}}
									fullWidth
								/>
								<Typography variant="caption" align="right" color="text.secondary">
									{storeDescription.length}/150 characters
								</Typography>

								<TextField
									label="Additional Keyword (optional)"
									value={keyword}
									onChange={(e) => setKeyword(e.target.value)}
									fullWidth
								/>
							</Fragment>
						)}

						{/* ═══════════════════ STEP 4 ═══════════════════ */}
						{step === "step4" && (
							<Fragment>
								<Typography
									variant="subtitle1"
									fontWeight={700}
									display="flex"
									alignItems="center"
									gap={1}
								>
									<Language fontSize="small" color="secondary"/>
									Choose Your Domain
								</Typography>

								{/* custom domain */}
								<Box sx={{display: "flex", gap: 0, alignItems: "stretch"}}>
									{/* https:// prefix box */}
									<Box
										sx={{
											px: 2,
											display: "flex",
											alignItems: "center",
											bgcolor: "#f0f0f0",
											border: "1px solid #cfcfcf",
											borderRight: 0,
											borderRadius: "4px 0 0 4px",
											typography: "body2",
											color: "text.secondary",
											whiteSpace: "nowrap",
										}}
									>
										https://
									</Box>
									<TextField
										label="Your Domain Name"
										value={baseDomain}
										inputProps={{maxLength: 30}}
										onChange={(e) => {
											// user only sees / edits the middle piece
											const mid = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "");

											setBaseDomain(mid);                                 // input box
											setDomain(mid ? `${mid}.microstores.net` : "");     // full host for APIs
											setHideGenerateButton(!!mid);

											// ─── debounce + abort ───
											if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

											debounceTimerRef.current = setTimeout(() => {
												if (domainControllerRef.current) domainControllerRef.current.abort();

												if (!mid) {
													setIsDomainAvailable(false);
													return;
												}

												const controller = new AbortController();
												domainControllerRef.current = controller;
												fetchDomainAvailability(mid, controller.signal);   // helper already adds https:// and suffix
											}, 2000);  // 1-second delay
										}}
										fullWidth
									/>
									<Box
										sx={{
											px: 2,
											display: "flex",
											alignItems: "center",
											bgcolor: "#f0f0f0",
											border: "1px solid #cfcfcf",
											borderLeft: 0,
											borderRadius: "0 4px 4px 0",
											typography: "body2",
											color: "text.secondary",
											whiteSpace: "nowrap",
										}}
									>
										.microstores.net
									</Box>
								</Box>
								<Typography variant="caption" color="text.secondary" sx={{mt: 0.5, display: "block"}}>
									This domain name cannot be changed once created.
								</Typography>
								{baseDomain && (
									<Typography
										variant="caption"
										display="flex"
										alignItems="center"
										gap={0.5}
										color={isDomainAvailable ? "success.main" : "error.main"}
									>
										{isDomainAvailable ? (
											<>
												<CheckCircle fontSize="small"/> Domain is available
											</>
										) : (
											<>
												<ErrorOutline fontSize="small"/> Domain not available
											</>
										)}
									</Typography>
								)}

								{/* generate AI suggestions */}
								<OutlinedButton
									onClick={() => setOpenConsent(true)}
									disabled={hideGenerateButton}
									sx={{mt: 2}}
								>
									<AutoAwesome sx={{mr: 1}} fontSize="small"/>
									{domainList.length ? "Regenerate" : "Generate AI Suggestions"}
								</OutlinedButton>

								{/* suggestions list */}
								{domainList.length > 0 && (
									<Box sx={{maxHeight: 250, overflowY: "auto", mt: 2, display: "flex", flexDirection: "column", gap: 1}}>
										{domainList.map((d, i) => (
											<CardActionArea
												key={i}
												sx={{
													p: 1.5,
													borderRadius: 2,
													border: domain === d ? "2px solid #2563eb" : "1px solid #e0e0e0",
													bgcolor: domain === d ? "#e8f0ff" : "background.paper",
												}}
												onClick={() => {
													const clean = normalizeStoreUrl(d);
													setDomain(clean);
													const mid = clean.replace(/^https:\/\//, "").replace(".microstores.net", "");
													setBaseDomain(mid);
													setIsDomainAvailable(true);
													setHideGenerateButton(true);
												}}
											>
												<Typography>{d}</Typography>
												{domain === d && <Check color="primary" sx={{position: "absolute", top: 8, right: 8}}/>}
											</CardActionArea>
										))}
									</Box>
								)}

								{domain && (
									<Box
										sx={{
											p: 2,
											mt: 2,
											bgcolor: "#eaf5ff",
											borderRadius: 2,
											display: "flex",
											alignItems: "center",
											gap: 1,
										}}
									>
										<Storefront color="primary"/>
										<Typography variant="body2">
											Your store will be available at{" "}
											<b>
												{domain.toLowerCase().startsWith("https://")
													? domain.toLowerCase()
													: `https://${domain.toLowerCase()}`}
											</b>
										</Typography>
									</Box>
								)}
							</Fragment>
						)}

						{/* ═══════════════════ STEP 5 ═══════════════════ */}
						{step === "step5" && (
							<Fragment>
								<Box sx={{display: "flex", justifyContent: "center", mb: 2}}>
									<CheckCircle sx={{fontSize: 64, color: "success.main"}}/>
								</Box>
								<Typography variant="h5" fontWeight={700} align="center">
									Welcome to Microstores!
								</Typography>
								<Typography variant="body2" align="center" color="text.secondary">
									Your vendor account has been created successfully.
								</Typography>
								<Button
									variant="contained" color="primary" sx={{mt: 4}}
									fullWidth onClick={() => navigate("/login")}>
									Sign In to Your Account
								</Button>
								<OutlinedButton fullWidth sx={{mt: 1}} onClick={() => navigate("/")}>
									Return to Home
								</OutlinedButton>
							</Fragment>
						)}

						{/* ═══════════════════ NAV BUTTONS (hidden on success) ═══════════════════ */}
						{step !== "step5" && (
							<Box sx={{display: "flex", justifyContent: "space-between", pt: 2}}>
								<OutlinedButton
									onClick={() =>
										step === "step1" ? navigate("/") : setStep(`step${Math.max(1, stepNum - 1)}`)
									}
									startIcon={<ArrowBackIosNew fontSize="small"/>}
								>
									Back
								</OutlinedButton>

								<Button variant='contained'
								        endIcon={<ArrowForwardIos fontSize="small"/>}
								        color="primary"
								        sx={{
									        fontWeight: 550,
								        }}
								        disabled={
									        (step === "step1" && !step1Valid) ||
									        (step === "step2" && otpValue.length !== 6) ||
									        (step === "step3" && !step3Valid) ||
									        (step === "step4" && (!domain || !isDomainAvailable))
								        }
								        onClick={() => {
									        /* ───────────────── continue-flow switch ───────────────── */
									        switch (step) {
										        case "step1": {
											        if (!passwordRegex.test(password)) {
												        setNotice({type: "error", message: "Password needs 8-20 chars: upper, lower, number & one of @ $ ! % * ? &. No other symbols."});
												        return;
											        }
											        setLoading(true);
											        fetch(
												        `${config.API_MWSVENDORONBOARDING_BASE_URL}/microstores/newVendorAccount`,
												        {
													        method: "POST",
													        body: JSON.stringify({
														        phoneNumber: "+" + countryDail + phoneNumber,
														        countryCode,
														        countryName,
														        emailAddress: email,
														        password,
														        confirmPassword,
													        }),
													        headers: {
														        Accept: "*/*",
														        "Cache-Control": "no-cache",
														        "Content-Type": "application/json",
													        },
												        }
											        )
												        .then((r) => r.json())
												        .then((d) => {
													        if (d.accountStatus === "INVALID_REQUEST") {
														        setNotice({type: "error", message: "Invalid details."});
														        return;
													        }
													        if (d.accountStatus === "VERIFIED") {
														        const msg = (d.message || "").toLowerCase();
														        const mentionsEmail = msg.includes("email address");
														        const mentionsPhone = msg.includes("phone number");
														        const which =
															        mentionsEmail && mentionsPhone
																        ? "this email and phone"
																        : mentionsEmail
																	        ? "this email"
																	        : mentionsPhone
																		        ? "this phone"
																		        : "the provided details";
														        setNotice({type: "error", message: `Account already exists for ${which}.`});
														        return;
													        }

													        const dupFlags = ["DUPLICATE_RECORD", "DUPLICATE_EMAIL", "DUPLICATE_PHONE"];
													        if (dupFlags.includes(d.accountStatus)) {
														        setNotice({
															        type: "error",
															        message: d.message || "This email or phone is already in use.",
														        });
														        return;
													        }

													        setVendorId(d.vendorId);
													        return fetch(
														        `${config.API_MWSVENDORONBOARDING_BASE_URL}/microstores/otp/send`,
														        {
															        method: "POST",
															        body: JSON.stringify({vendorId: d.vendorId}),
															        headers: {
																        Accept: "*/*",
																        "Cache-Control": "no-cache",
																        "Content-Type": "application/json",
															        },
														        }
													        ).then((r2) => r2.json());
												        })
												        .then((d2) => {
													        if (d2 && d2.statusCode === 200) {
														        setStep("step2");
														        startTimer();
													        } else if (d2 && d2.statusCode === 403) {
														        setNotice({type: "error", message: d2.message || "Please wait before requesting another code."});
														        setStep("step2");
														        startTimer();
													        } else if (d2) {
														        setNotice({type: "error", message: "Error sending OTP. Please try again."});
													        }
												        })
												        .catch(() => setNotice({type: "error", message: "Network error. Please try again."}))
												        .finally(() => setLoading(false));
											        break;
										        }
										        case "step2": {
											        setLoading(true);
											        fetch(
												        `${config.API_MWSVENDORONBOARDING_BASE_URL}/microstores/otp/verify`,
												        {
													        method: "POST",
													        body: JSON.stringify({vendorId, otp: otpValue}),
													        headers: {
														        Accept: "*/*",
														        "Cache-Control": "no-cache",
														        "Content-Type": "application/json",
													        },
												        }
											        )
												        .then((r) => r.json())
												        .then((d) => {
													        if (d.optVerifyStatusCode !== 1000) {
														        const info = resolveOtpStatus(d.optVerifyStatusCode);
														        setOtp(Array(6).fill(""));
														        throw new Error(info.message);
													        }
													        // ─── OTP verified, now login ───
													        return fetch(
														        `${config.API_MWSVENDORONBOARDING_BASE_URL}/microstores/vendorLogin`,
														        {
															        method: "POST",
															        body: JSON.stringify({username: email, password}),
															        headers: {
																        Accept: "*/*",
																        "Cache-Control": "no-cache",
																        "Content-Type": "application/json",															        },
														        }
													        ).then((r2) => r2.json());
												        })
												        .then((login) => {
													        if (login.authenticated) {
														        let normalizedExpiry = normalizeSessionExpiry(login.expiresAt);
														        if (!normalizedExpiry) {
															        normalizedExpiry = new Date(Date.now() + 60 * 60 * 1000).toISOString();
														        }

														        setSessionToken(login.sessionToken);
														        setVendorId(login.vendorId);
														        setAuthSession({
															        vendorId: login.vendorId,
															        sessionToken: login.sessionToken,
															        sessionStatus: login.sessionStatus,
															        userName: login.userName,
															        expiresAt: normalizedExpiry,
															        rememberMe: false,
														        });
														        setStep("step3");
													        } else {
														        throw new Error("Login failed. Please try again.");
													        }
												        })
												        .catch((err) => {
													        setNotice({type: "error", message: err.message});
												        })
												        .finally(() => setLoading(false));
											        break;
										        }
										        case "step3":
											        setStep("step4");
											        break;
										        case "step4": {
											        const createProfile = async () => {
												        try {
													        setLoading(true);

													        // 1) createVendorProfile
													        const r1 = await fetch(
														        `${config.API_MWSVENDORONBOARDING_BASE_URL}/microstores/createVendorProfile`,
														        {
															        method: "POST",
															        body: JSON.stringify({
																        sessionToken,
																        vendorId,
																        businessName,
																        firstName,
																        lastName,
																        addressLine1: address1,
																        addressLine2: address2,
																        city,
																        state,
																        postalCode: zipCode,
																        country,
															        }),
															        headers: {
																        Accept: "*/*",
																        "Cache-Control": "no-cache",
																        "Content-Type": "application/json",
															        },
														        }
													        );

													        if (!r1.ok) {
														        const errBody = await r1.text().catch(() => "");
														        throw new Error(errBody || "Failed to create vendor profile.");
													        }

													        // 2) updateStoreFrontWebInfo
													        const r2 = await fetch(
														        `${config.API_MWSVENDORONBOARDING_BASE_URL}/microstores/updateStoreFrontWebInfo`,
														        {
															        method: "POST",
															        body: JSON.stringify({
																        sessionToken,
																        vendorId,
																        businessCategory: category,
																        storeType,
																        storeDescription,
																        storeFrontWeb: normalizeStoreUrl(domain)
															        }),
															        headers: {
																        Accept: "*/*",
																        "Cache-Control": "no-cache",
																        "Content-Type": "application/json",
															        },
														        }
													        );

													        if (!r2.ok) {
														        const errBody = await r2.text().catch(() => "");
														        throw new Error(errBody || "Failed to update StoreFront Web info.");
													        }

													        // success
													        setStep("step5");
												        } catch (e) {
													        setNotice({
														        type: "error",
														        message:
															        (e && e.message) ||
															        "Something went wrong. Please review your details and submit again.",
													        });
													        // stay on step4
												        } finally {
													        setLoading(false);
												        }
											        };

											        createProfile();
											        break;
										        }
										        default:
											        break;
									        }
								        }}
								>
									{step === "step4" ? "Complete Registration" : "Continue"}
								</Button>
							</Box>
						)}
					</CardContent>
				</Card>

				{/* ═══════════════ domain consent dialog ═══════════════ */}
				<Dialog open={openConsent} TransitionComponent={Transition} onClose={() => setOpenConsent(false)} maxWidth="sm">
					<DialogTitle fontWeight={600}>AI Domain Generation Consent</DialogTitle>
					<DialogContent>
						<DialogContentText>
							We can generate personalised domain suggestions using your business information. Would you like to include
							your personal name in the domain suggestions?
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button
							color="secondary"
							sx={{fontWeight: 550}}
							onClick={() => {
								setOpenConsent(false);
								handleGenerateDomain("disagree");
							}}
						>
							No, exclude personal info
						</Button>
						<Button
							variant="contained" color="primary"
							onClick={() => {
								setOpenConsent(false);
								handleGenerateDomain("agree");
							}}
						>
							Yes, include my name
						</Button>
					</DialogActions>
				</Dialog>

				<Collapse in={Boolean(notice.message)}>
					<Alert
						icon={
							notice.type === "success" ? (
								<CheckCircle fontSize="inherit"/>
							) : (
								<ErrorOutline fontSize="inherit"/>
							)
						}
						severity={notice.type === "success" ? "success" : "error"}
						sx={{mt: 2}}
						onClose={() => setNotice({type: "", message: ""})}
					>
						{notice.message}
					</Alert>
				</Collapse>

				<Backdrop open={loading}>
					<CircularProgress sx={{color: 'white'}}/>
				</Backdrop>
			</ThemeProvider>
		</Box>
	);
}
